import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { showError, showWarning } from '../utils/sweetAlert';
import { Skeleton } from './ui/skeleton';
import logger from '../utils/logger';
import OverviewTab from './ProjectDetail/OverviewTab';
import TasksTab from './ProjectDetail/TasksTab';
import FundingTab from './ProjectDetail/FundingTab';
import PartnersTab from './ProjectDetail/PartnersTab';
import MVTab from './ProjectDetail/MVTab';
import FilesTab from './ProjectDetail/FilesTab';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [newTask, setNewTask] = useState({ title: '', due_date: '', notes: '' });
  const [showTaskForm, setShowTaskForm] = useState(false);

  const PROJECT_STATUSES = [
    'planned', 'scoping', 'funding', 'approved', 'in_progress', 
    'commissioning', 'm_and_v', 'completed', 'archived'
  ];

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/comprehensive/${projectId}`);
      if (response.ok) {
        const data = await response.json();
        setProject(data);
      } else {
        logger.error('Failed to fetch project');
      }
    } catch (error) {
      logger.error('Error fetching project:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateProjectStatus = async (newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/comprehensive/${projectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        fetchProject();
      } else {
        const error = await response.json();
        showError('Update Failed', `Failed to update status: ${error.detail}`);
      }
    } catch (error) {
      logger.error('Error updating project status:', error);
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/projects/${projectId}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTask)
      });
      
      if (response.ok) {
        setNewTask({ title: '', due_date: '', notes: '' });
        setShowTaskForm(false);
        fetchProject();
      }
    } catch (error) {
      logger.error('Error creating task:', error);
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/projects/${projectId}/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        fetchProject();
      }
    } catch (error) {
      logger.error('Error updating task:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'planned': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'scoping': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'funding': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'approved': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'in_progress': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      'commissioning': 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
      'm_and_v': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      'completed': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
      'archived': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  const getTaskStatusColor = (status) => {
    const colors = {
      'todo': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      'doing': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'blocked': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'done': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  const getProgressPercentage = (status) => {
    const statusIndex = PROJECT_STATUSES.indexOf(status);
    return statusIndex >= 0 ? ((statusIndex + 1) / PROJECT_STATUSES.length) * 100 : 0;
  };

  const overviewTabProject = {
    ...project,
    onStatusChange: (newStatus) => updateProjectStatus(newStatus),
  };

  // Render loading with skeleton placeholders
  if (isLoading) {
    return (
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header Skeleton */}
          <Skeleton className="w-1/2 h-10 mb-4 rounded bg-gray-200 dark:bg-gray-700" />
          <Skeleton className="w-full h-4 mb-12 rounded bg-gray-200 dark:bg-gray-700" />
          {/* Tabs Skeleton */}
          <Skeleton className="w-full h-8 mb-6 rounded bg-gray-200 dark:bg-gray-700" />
          {/* Main Content */}
          <Skeleton className="w-full h-[300px] rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12 text-gray-700 dark:text-gray-300">Project not found</div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'tasks', name: 'Tasks', icon: '✅' },
    { id: 'funding', name: 'Funding', icon: '💰' },
    { id: 'partners', name: 'Partners', icon: '🤝' },
    { id: 'mv', name: 'M&V', icon: '📈' },
    { id: 'files', name: 'Files & Notes', icon: '📁' }
  ];

  return (
   <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-2">
            <button
              onClick={() => window.history.back()}
              className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
            >
              ← Back
            </button>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{project.title}</h1>
            <span
              className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(project.status)}`}
            >
              {project.status.replace('_', ' & ').toUpperCase()}
            </span>
          </div>
          <p className="text-gray-600 dark:text-gray-400">{project.description}</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
                >
                  <span>{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          {activeTab === 'overview' && (
            <OverviewTab
              project={overviewTabProject}
              projectStatuses={PROJECT_STATUSES}
              getStatusColor={getStatusColor}
              getProgressPercentage={getProgressPercentage}
            />
          )}
          {activeTab === 'tasks' && (
            <TasksTab
              project={project}
              showTaskForm={showTaskForm}
              newTask={newTask}
              onShowTaskForm={() => setShowTaskForm(true)}
              onHideTaskForm={() => setShowTaskForm(false)}
              onNewTaskChange={setNewTask}
              onCreateTask={createTask}
              onUpdateTaskStatus={updateTaskStatus}
            />
          )}
          {activeTab === 'funding' && <FundingTab project={project} />}
          {activeTab === 'partners' && <PartnersTab project={project} />}
          {activeTab === 'mv' && <MVTab project={project} />}
          {activeTab === 'files' && <FilesTab project={project} />}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
